import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { PatientRepositoryService, PracticePatientFlag } from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientFlagsStore — per-patient flag assignments.
 *
 * Owns the list of practice flags currently applied to the patient in
 * `PatientStore.current()`. Auto-loads when the current patient changes and
 * clears when the patient goes null. Mutations live on `PatientStore`
 * (`addFlag` / `removeFlag` / `updatePatientFlags`) since they're conceptually
 * patient-level commands; this store reflects the resulting state.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientFlagsStore {
  private patientStore = inject(PatientStore);
  private patientRepo = inject(PatientRepositoryService);

  private readonly _items = signal<PracticePatientFlag[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }
      this.fetch(patient.id);
    });
  }

  /**
   * Re-fetch flags for the current patient. No-op if no current patient.
   */
  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.patientRepo.loadFlagsForPatient(patientId).subscribe({
      next: flags => {
        this._items.set(flags);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient flags');
        
      }
    });
  }
}
